import { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Stack,
} from '@mui/material';
import { Lock, LogOut, AlertCircle } from 'lucide-react';
import { PinInput } from './PinInput';
import { useAppLock } from '../../contexts/AppLockContext';
import { api } from '../../lib/api';
import type { PinVerificationResponse } from '../../types/security';

/**
 * Lock Screen Overlay Component
 * Displays when app is locked due to inactivity or background
 */
export function LockScreen() {
  const { isLocked, unlockApp, forceLogout, settings } = useAppLock();
  const [, setPin] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [isTemporarilyLocked, setIsTemporarilyLocked] = useState(false);

  // Reset error when dialog opens
  useEffect(() => {
    if (isLocked) {
      setError(null);
      setPin('');
      setIsTemporarilyLocked(settings?.isTemporarilyLocked ?? false);
    }
  }, [isLocked, settings]);

  const handlePinComplete = async (enteredPin: string) => {
    setPin(enteredPin);
    setError(null);
    setIsVerifying(true);

    try {
      const response = await api.security.verifyPin({ pin: enteredPin });
      
      if (!response.data) {
        throw new Error('No response from server');
      }

      const result: PinVerificationResponse = response.data;

      if (result.verified) {
        // Successful unlock
        unlockApp();
      } else {
        // Failed verification
        setError(result.message);
        setRemainingAttempts(result.remainingAttempts);
        setIsTemporarilyLocked(result.isTemporarilyLocked);
        setPin('');

        // Force logout if max attempts exceeded
        if (result.shouldForceLogout) {
          setTimeout(() => {
            forceLogout();
          }, 2000);
        }
      }
    } catch (error: any) {
      console.error('PIN verification error:', error);
      setError(error?.message || 'Failed to verify PIN. Please try again.');
      setPin('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClearPin = () => {
    setPin('');
    setError(null);
  };

  const handleLogout = () => {
    forceLogout();
  };

  if (!isLocked) {
    return null;
  }

  return (
    <Dialog
      open={isLocked}
      fullScreen
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      <Paper
        elevation={24}
        sx={{
          padding: { xs: 2, sm: 3, md: 4 },
          maxWidth: { xs: '95%', sm: 420, md: 450 },
          width: '100%',
          margin: { xs: 1, sm: 2 },
          borderRadius: { xs: 2, sm: 3, md: 4 },
          textAlign: 'center',
        }}
      >
        <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} alignItems="center">
          {/* Lock Icon */}
          <Box
            sx={{
              width: { xs: 60, sm: 70, md: 80 },
              height: { xs: 60, sm: 70, md: 80 },
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Lock size={36} color="white" />
          </Box>

          {/* Title */}
          <Box>
            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              App Locked
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}>
              Enter your PIN to unlock
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" icon={<AlertCircle size={20} />} sx={{ width: '100%' }}>
              <Typography variant="body2" fontWeight={600}>
                {error}
              </Typography>
              {remainingAttempts !== null && remainingAttempts > 0 && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining
                </Typography>
              )}
            </Alert>
          )}

          {/* Temporary Lock Warning */}
          {isTemporarilyLocked && (
            <Alert severity="warning" sx={{ width: '100%' }}>
              <Typography variant="body2" fontWeight={600}>
                Account temporarily locked
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Please wait 5 minutes before trying again
              </Typography>
            </Alert>
          )}

          {/* PIN Input */}
          <Box sx={{ py: { xs: 1, sm: 1.5, md: 2 }, width: '100%', display: 'flex', justifyContent: 'center' }}>
            {isVerifying ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <PinInput
                length={settings?.hasPin ? 6 : 4}
                onComplete={handlePinComplete}
                onClear={handleClearPin}
                error={!!error}
                disabled={isTemporarilyLocked}
                autoFocus
              />
            )}
          </Box>

          {/* Help Text */}
          <Typography variant="caption" color="text.secondary">
            Enter {settings?.hasPin ? '4-6' : '4'} digit PIN to unlock
          </Typography>

          {/* Logout Button */}
          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider', width: '100%' }}>
            <IconButton
              onClick={handleLogout}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                  backgroundColor: 'error.light',
                },
              }}
            >
              <LogOut size={20} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Logout
              </Typography>
            </IconButton>
          </Box>
        </Stack>
      </Paper>
    </Dialog>
  );
}
